import { connect } from "react-redux";
import { useEffect, useState } from "react";
import Input from "src/components/Shared/Forms/Inputs/Input";
import { apiRequest } from "src/async/apiUtils";
import { toast } from "react-toastify";
import SelectMenu from "src/components/Shared/Forms/Selects/SelectMenu";
import RadioGroup from "src/components/Shared/Forms/Inputs/RadioGroup";
import InformationAlert from "src/components/Shared/Alerts/InformationAlert";
import Button from "src/components/Shared/Buttons/Button";
import SlideOver from "src/components/Shared/SlideOver";
import { clientTypes } from "src/helpers/clientHelper";
import useFetch from "src/hooks/useFetch";

const ClientAddModal = ({ isOpen, onCancel = () => {}, onSuccess = () => {}, isExistingClient }) => {
  const formJson = {
    name: "",
    email: "",
    domain: "",
    password: "",
    company_name: "",
    client_type: "paid",
    confirm_password: "",
    send_welcome_email: true,
    existing_client: isExistingClient,
    ...(isExistingClient ? { database_url: "" } : {}),
  };

  const {
    response: { data: plans },
  } = useFetch(isOpen ? `/plans` : null);

  const [formData, setFormData] = useState(formJson);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const onHandleChange = (key, value) => {
    setFormData((prevData) => ({ ...prevData, [key]: value }));
  };

  const onHandleSubmit = async () => {
    try {
      setIsSubmitting(true);
      const { data } = await apiRequest("POST", `/clients`, { body: formData });
      toast.success(data.message);
      onSuccess();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      setFormData(formJson);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  return (
    <>
      <SlideOver
        isOpen={isOpen}
        handleClose={onCancel}
        title={isExistingClient ? "Existing Client" : "Client"}
        description="View, update, and modify client details and information below."
        size="3xl"
        footerComponent={
          <div className="flex w-full justify-end gap-x-3 ">
            <Button
              version="gray"
              onClick={onCancel}
              disabled={isSubmitting}>
              Cancel
            </Button>
            <Button
              version="primary"
              type="button"
              onClick={onHandleSubmit}
              disabled={isSubmitting}
              loading={isSubmitting}>
              Save
            </Button>
          </div>
        }>
        <div className="flex w-full flex-wrap gap-y-4">
          {!isExistingClient && (
            <>
              <div className="relative w-full">
                <Input
                  inline={true}
                  label={"Name"}
                  wrapperClass={"bg-white"}
                  placeholder={"John Doe"}
                  value={formData?.name}
                  onChange={(e) => onHandleChange("name", e.target.value)}
                />
              </div>
              <div className="relative w-full">
                <Input
                  inline={true}
                  label={"Work email"}
                  wrapperClass={"bg-white"}
                  placeholder={"youlooknicetoday@company.com"}
                  value={formData?.email}
                  onChange={(e) => onHandleChange("email", e.target.value)}
                />
              </div>
            </>
          )}
          <div className="relative w-full">
            <Input
              inline={true}
              label={"Company name"}
              wrapperClass={"bg-white"}
              placeholder={"Google Inc."}
              value={formData?.company_name}
              onChange={(e) => onHandleChange("company_name", e.target.value)}
            />
          </div>
          <div className="relative w-full">
            <SelectMenu
              inline
              label={"Client Type"}
              defaultText="Select a plan"
              startIndex={clientTypes?.findIndex((c_type) => c_type?.value === formData?.client_type)}
              options={clientTypes}
              setOption={(option) => {
                onHandleChange("client_type", option.value);
              }}
            />
          </div>
          {formData?.client_type === "subscription" && (
            <div className="relative w-full">
              <SelectMenu
                inline
                label={"Subscription Plan"}
                defaultText="Select a plan"
                startIndex={plans?.findIndex((c_type) => c_type?._id === formData?.plan_id)}
                options={plans?.map((item) => ({ key: item?.name, value: item?._id }))}
                setOption={(option) => {
                  onHandleChange("plan_id", option.value);
                }}
              />
            </div>
          )}
          {isExistingClient && (
            <div className="relative w-full">
              <Input
                inline={true}
                label={"Database URL"}
                wrapperClass={"bg-white"}
                placeholder="e.g., mongodb://username:password@host:port/database"
                value={formData?.database_url}
                onChange={(e) => onHandleChange("database_url", e.target.value)}
              />
            </div>
          )}

          {!formData?.existing_client && (
            <>
              <div className="relative w-full">
                <RadioGroup
                  title=""
                  subtitle=""
                  options={[
                    { id: "send-email", title: "Send invite email" },
                    { id: "manual", title: "Manually set password" },
                  ]}
                  defaultChecked={formData?.send_welcome_email ? "send-email" : "manual"}
                  onChange={(id) => {
                    onHandleChange("send_welcome_email", id === "send-email");
                  }}
                  containerClasses="ml-1"
                  layout="horizontal"
                />
              </div>

              {formData?.send_welcome_email && (
                <div className="animate-fade-in w-full">
                  <InformationAlert
                    type="default"
                    content="An invite email will be sent to the user's email address with a link to set up their password."
                  />
                </div>
              )}
              {!formData?.send_welcome_email && (
                <div className="animate-fade-in w-full">
                  <div className="flex w-full gap-x-4 gap-y-2">
                    <div className="w-full">
                      <Input
                        name="password"
                        label="Password"
                        type="password"
                        placeholder="**************"
                        inline
                        value={formData?.password}
                        autoComplete="new-password"
                        onChange={(e) => onHandleChange("password", e.target.value.replace(/\s/g, ""))}
                      />
                    </div>
                    <div className="w-full">
                      <Input
                        name="confirm-password"
                        label="Confirm Password"
                        placeholder="**************"
                        type="password"
                        inline
                        value={formData.confirm_password}
                        autoComplete="new-password"
                        onChange={(e) => onHandleChange("confirm_password", e.target.value.replace(/\s/g, ""))}
                      />
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </SlideOver>
    </>
  );
};

const mapStateToProps = (state) => {
  return {
    user: state.auth.user,
    site: state.site,
  };
};

export default connect(mapStateToProps, {})(ClientAddModal);
